import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';

interface DeleteStudentConfirmDialogProps {
  open: boolean;
  studentName: string;
  studentNumber: string;
  errorMessage: string | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/** Super Admin-only hard delete (Phase 66) — modeled on
 * ConvertToStudentConfirmDialog.tsx, the codebase's one existing
 * confirmation-dialog convention (Dialog/DialogTitle/DialogContent with
 * an error Alert + summary/DialogActions with Cancel + a disabled-while-
 * submitting primary button), reused here rather than reinvented. */
export const DeleteStudentConfirmDialog = ({
  open,
  studentName,
  studentNumber,
  errorMessage,
  submitting,
  onClose,
  onConfirm,
}: DeleteStudentConfirmDialogProps) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Delete student</DialogTitle>
    <DialogContent>
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />}>
          This permanently deletes the student and every related application,
          payment, document, agreement and batch record. This cannot be undone.
        </Alert>
        <Typography variant="subtitle1">
          {studentName} ({studentNumber})
        </Typography>
        <Typography variant="body2">
          Are you sure you want to permanently delete this student?
        </Typography>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" color="error" disabled={submitting} onClick={onConfirm}>
        {submitting ? 'Deleting…' : 'Delete student'}
      </Button>
    </DialogActions>
  </Dialog>
);
